import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { toLocalYMD } from '../utils/dateUtils';
import {
  Users,
  Phone,
  Clock,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  X,
  TrendingUp,
  Plane,
  Home,
  Target,
  BarChart3,
  Layers,
  Sigma,
  CalendarRange
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const StaffingLevels = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [weeksBack, setWeeksBack] = useState(4);
  const [weeksForward, setWeeksForward] = useState(4);
  const [selectedDay, setSelectedDay] = useState(null);
  const [viewMode, setViewMode] = useState('daily'); // 'daily' or 'weekly'
  const [breakdownMode, setBreakdownMode] = useState('type'); // 'type' or 'role'
  const [showTarget, setShowTarget] = useState(true);
  const [targetValue, setTargetValue] = useState(0);
  const [mainTab, setMainTab] = useState('levels'); // 'levels' or 'averages'

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/staffing-levels`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { weeks_back: weeksBack, weeks_forward: weeksForward }
      });
      setData(response.data);
      if (response.data.min_staffing_target > 0) {
        setTargetValue(response.data.min_staffing_target);
      }
    } catch (e) {
      console.error('Failed to load staffing levels:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [weeksBack, weeksForward]);

  const formatDate = (dateStr) => {
    const [y, m, d] = dateStr.split('-');
    return new Date(parseInt(y), parseInt(m) - 1, parseInt(d)).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric'
    });
  };

  const formatDateFull = (dateStr) => {
    const [y, m, d] = dateStr.split('-');
    return new Date(parseInt(y), parseInt(m) - 1, parseInt(d)).toLocaleDateString('en-US', {
      weekday: 'long', month: 'long', day: 'numeric', year: 'numeric'
    });
  };

  // Compute max values for chart scaling
  const maxCount = useMemo(() => {
    if (!data) return 1;
    if (viewMode === 'daily') {
      return Math.max(...data.days.map(d => d.regular.count + d.on_call.count + d.pto.count), 1);
    }
    return Math.max(...data.weeks.map(w => w.avg_regular + w.avg_oncall + w.avg_pto), 1);
  }, [data, viewMode]);

  const roleColors = {
    admin: { bg: 'bg-purple-400', text: 'text-purple-700', light: 'bg-purple-100' },
    manager: { bg: 'bg-indigo-400', text: 'text-indigo-700', light: 'bg-indigo-100' },
    team_lead: { bg: 'bg-cyan-400', text: 'text-cyan-700', light: 'bg-cyan-100' },
    tier1: { bg: 'bg-blue-400', text: 'text-blue-700', light: 'bg-blue-100' },
    tier2: { bg: 'bg-green-400', text: 'text-green-700', light: 'bg-green-100' },
    solution_engineer: { bg: 'bg-teal-400', text: 'text-teal-700', light: 'bg-teal-100' },
    tam: { bg: 'bg-rose-400', text: 'text-rose-700', light: 'bg-rose-100' },
    employee: { bg: 'bg-gray-400', text: 'text-gray-700', light: 'bg-gray-100' },
  };

  const getRoleColor = (role) => roleColors[role] || { bg: 'bg-gray-400', text: 'text-gray-700', light: 'bg-gray-100' };

  const CHART_HEIGHT = 220;

  if (loading && !data && mainTab === 'levels') {
    return (
      <div className="space-y-4" data-testid="staffing-levels">
        <MainTabBar mainTab={mainTab} setMainTab={setMainTab} />
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="staffing-levels">
      <MainTabBar mainTab={mainTab} setMainTab={setMainTab} />

      {mainTab === 'averages' ? (
        <StaffingAverages />
      ) : !data ? (
        <p className="text-sm text-gray-400 py-8 text-center">No staffing data available.</p>
      ) : (
      <>
      {/* Controls Bar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center space-x-3">
          {/* View Mode */}
          <div className="inline-flex rounded-lg border border-gray-300 bg-white p-0.5">
            <button
              onClick={() => setViewMode('daily')}
              data-testid="view-daily"
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                viewMode === 'daily' ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <BarChart3 className="h-3.5 w-3.5 inline mr-1" />Daily
            </button>
            <button
              onClick={() => setViewMode('weekly')}
              data-testid="view-weekly"
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                viewMode === 'weekly' ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Layers className="h-3.5 w-3.5 inline mr-1" />Weekly Avg
            </button>
          </div>

          {/* Breakdown Mode */}
          <div className="inline-flex rounded-lg border border-gray-300 bg-white p-0.5">
            <button
              onClick={() => setBreakdownMode('type')}
              data-testid="breakdown-type"
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                breakdownMode === 'type' ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              By Type
            </button>
            <button
              onClick={() => setBreakdownMode('role')}
              data-testid="breakdown-role"
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                breakdownMode === 'role' ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              By Role
            </button>
          </div>

          {/* Target Line Toggle */}
          <button
            onClick={() => setShowTarget(!showTarget)}
            data-testid="toggle-target"
            className={`flex items-center space-x-1 px-3 py-1.5 text-xs font-medium rounded-lg border transition-all ${
              showTarget ? 'border-red-300 bg-red-50 text-red-700' : 'border-gray-300 bg-white text-gray-600'
            }`}
          >
            <Target className="h-3.5 w-3.5" />
            <span>Target</span>
          </button>
        </div>

        <div className="flex items-center space-x-3">
          {/* Time Range */}
          <div className="flex items-center space-x-2 text-sm">
            <button onClick={() => setWeeksBack(Math.min(weeksBack + 2, 12))} className="text-gray-400 hover:text-gray-600">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-xs text-gray-500 whitespace-nowrap">{weeksBack}w back</span>
            <span className="text-gray-300">|</span>
            <span className="text-xs text-gray-500 whitespace-nowrap">{weeksForward}w ahead</span>
            <button onClick={() => setWeeksForward(Math.min(weeksForward + 2, 12))} className="text-gray-400 hover:text-gray-600">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          <Button onClick={fetchData} variant="outline" size="sm" disabled={loading} data-testid="refresh-staffing">
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Target Input */}
      {showTarget && (
        <div className="flex items-center space-x-3 px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
          <Target className="h-4 w-4 text-red-500" />
          <span className="text-xs text-red-700 font-medium">Min Staffing Target:</span>
          <input
            type="number"
            value={targetValue}
            onChange={(e) => setTargetValue(parseInt(e.target.value) || 0)}
            className="w-16 px-2 py-1 text-xs border border-red-300 rounded text-center"
            min="0"
            data-testid="target-input"
          />
          <span className="text-xs text-red-500">employees/day</span>
        </div>
      )}

      {/* Main Chart */}
      <Card>
        <CardContent className="pt-4 pb-4">
          <div className="overflow-x-auto">
            {viewMode === 'daily' ? (
              <DailyChart
                days={data.days}
                roles={data.roles}
                maxCount={maxCount}
                chartHeight={CHART_HEIGHT}
                breakdownMode={breakdownMode}
                showTarget={showTarget}
                targetValue={targetValue}
                selectedDay={selectedDay}
                onSelectDay={setSelectedDay}
                getRoleColor={getRoleColor}
                formatDate={formatDate}
              />
            ) : (
              <WeeklyChart
                weeks={data.weeks}
                maxCount={maxCount}
                chartHeight={CHART_HEIGHT}
                showTarget={showTarget}
                targetValue={targetValue}
                formatDate={formatDate}
              />
            )}
          </div>

          {/* Legend */}
          <div className="flex flex-wrap items-center gap-4 mt-4 pt-3 border-t border-gray-100">
            {breakdownMode === 'type' ? (
              <>
                <LegendItem color="bg-blue-400" label="Regular" />
                <LegendItem color="bg-orange-400" label="On-Call" />
                <LegendItem color="bg-yellow-400" label="PTO" />
              </>
            ) : (
              data.roles.map(role => (
                <LegendItem key={role} color={getRoleColor(role).bg} label={role} />
              ))
            )}
            {showTarget && targetValue > 0 && (
              <div className="flex items-center space-x-1.5">
                <div className="w-6 h-0 border-t-2 border-dashed border-red-500" />
                <span className="text-xs text-gray-500">Target ({targetValue})</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Day Drill-Down Panel */}
      {selectedDay && (
        <DayDrillDown
          day={data.days.find(d => d.date === selectedDay)}
          onClose={() => setSelectedDay(null)}
          formatDateFull={formatDateFull}
          getRoleColor={getRoleColor}
        />
      )}

      {/* Week-over-Week Comparison Table */}
      {viewMode === 'weekly' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center space-x-2">
              <TrendingUp className="h-4 w-4 text-blue-600" />
              <span>Week-over-Week Comparison</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="week-comparison-table">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-2 px-3 text-gray-600 font-medium">Week</th>
                    <th className="text-left py-2 px-3 text-gray-600 font-medium">Dates</th>
                    <th className="text-center py-2 px-3 text-gray-600 font-medium">Avg Regular</th>
                    <th className="text-center py-2 px-3 text-gray-600 font-medium">Avg On-Call</th>
                    <th className="text-center py-2 px-3 text-gray-600 font-medium">Avg PTO</th>
                    <th className="text-center py-2 px-3 text-gray-600 font-medium">Change</th>
                  </tr>
                </thead>
                <tbody>
                  {data.weeks.map((week, i) => {
                    const prev = i > 0 ? data.weeks[i - 1] : null;
                    const change = prev ? Math.round((week.avg_regular - prev.avg_regular) * 10) / 10 : 0;
                    return (
                      <tr key={week.week_number} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-2 px-3 font-medium">Wk {week.week_number}</td>
                        <td className="py-2 px-3 text-gray-600 text-xs">{formatDate(week.start_date)} - {formatDate(week.end_date)}</td>
                        <td className="py-2 px-3 text-center">
                          <span className="font-medium text-blue-700">{week.avg_regular}</span>
                        </td>
                        <td className="py-2 px-3 text-center">
                          <span className="font-medium text-orange-700">{week.avg_oncall}</span>
                        </td>
                        <td className="py-2 px-3 text-center">
                          <span className="font-medium text-yellow-700">{week.avg_pto}</span>
                        </td>
                        <td className="py-2 px-3 text-center">
                          {prev ? (
                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                              change > 0 ? 'bg-green-100 text-green-700' :
                              change < 0 ? 'bg-red-100 text-red-700' :
                              'bg-gray-100 text-gray-600'
                            }`}>
                              {change > 0 ? '+' : ''}{change}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">-</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
      </>
      )}
    </div>
  );
};

const MainTabBar = ({ mainTab, setMainTab }) => (
  <div className="inline-flex rounded-lg border border-gray-300 bg-white p-0.5" data-testid="staffing-main-tabs">
    <button
      onClick={() => setMainTab('levels')}
      data-testid="tab-levels"
      className={`px-4 py-1.5 text-xs font-medium rounded-md transition-all ${
        mainTab === 'levels' ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-50'
      }`}
    >
      <BarChart3 className="h-3.5 w-3.5 inline mr-1" />Staffing Levels
    </button>
    <button
      onClick={() => setMainTab('averages')}
      data-testid="tab-averages"
      className={`px-4 py-1.5 text-xs font-medium rounded-md transition-all ${
        mainTab === 'averages' ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-50'
      }`}
    >
      <Sigma className="h-3.5 w-3.5 inline mr-1" />Averages
    </button>
  </div>
);

const LegendItem = ({ color, label }) => (
  <div className="flex items-center space-x-1.5">
    <div className={`w-3 h-3 rounded ${color}`} />
    <span className="text-xs text-gray-500 capitalize">{label.replace('_', ' ')}</span>
  </div>
);

const DailyChart = ({ days, roles, maxCount, chartHeight, breakdownMode, showTarget, targetValue, selectedDay, onSelectDay, getRoleColor, formatDate }) => {
  return (
    <div className="relative">
      {/* Y-axis labels */}
      <div className="flex">
        <div className="w-8 flex flex-col justify-between pr-1" style={{ height: `${chartHeight}px` }}>
          <span className="text-[10px] text-gray-400 text-right">{maxCount}</span>
          <span className="text-[10px] text-gray-400 text-right">{Math.round(maxCount / 2)}</span>
          <span className="text-[10px] text-gray-400 text-right">0</span>
        </div>

        <div className="flex-1 relative">
          {/* Target line */}
          {showTarget && targetValue > 0 && targetValue <= maxCount && (
            <div
              className="absolute left-0 right-0 border-t-2 border-dashed border-red-400 z-10 pointer-events-none"
              style={{ bottom: `${(targetValue / maxCount) * chartHeight}px` }}
            >
              <span className="absolute -top-3 right-0 text-[10px] text-red-500 bg-white px-1">{targetValue}</span>
            </div>
          )}

          {/* Bars */}
          <div className="flex items-end space-x-0.5 min-w-fit" style={{ height: `${chartHeight}px` }}>
            {days.map((day) => {
              const isSelected = selectedDay === day.date;
              const isToday = day.is_today;
              const isWeekStart = day.day_of_week === 'Monday';
              const total = day.regular.count + day.on_call.count + day.pto.count;
              const belowTarget = showTarget && targetValue > 0 && day.regular.count < targetValue;
              const barAreaHeight = chartHeight - 40;
              
              const regularH = Math.round((day.regular.count / maxCount) * barAreaHeight);
              const oncallH = Math.round((day.on_call.count / maxCount) * barAreaHeight);
              const ptoH = Math.round((day.pto.count / maxCount) * barAreaHeight);
              const totalH = regularH + oncallH + ptoH;
              
              const barWidth = days.length > 30 ? 22 : 30;

              return (
                <div
                  key={day.date}
                  className={`flex flex-col items-center cursor-pointer group ${isWeekStart ? 'ml-3' : ''}`}
                  style={{ minWidth: `${barWidth + 8}px` }}
                  onClick={() => onSelectDay(isSelected ? null : day.date)}
                  data-testid={`bar-${day.date}`}
                >
                  {/* Hover count */}
                  <div className="opacity-0 group-hover:opacity-100 text-[10px] text-gray-600 font-medium mb-0.5 whitespace-nowrap" style={{ height: '14px' }}>
                    {total}
                  </div>
                  
                  {/* Bar stack - bottom aligned */}
                  <div className="relative" style={{ width: `${barWidth}px`, height: `${barAreaHeight}px` }}>
                    {breakdownMode === 'type' ? (
                      <>
                        {/* PTO on top */}
                        {ptoH > 0 && (
                          <div
                            className={`absolute left-0 right-0 rounded-t ${day.is_past ? 'bg-yellow-300' : 'bg-yellow-400'}`}
                            style={{ bottom: `${regularH + oncallH}px`, height: `${ptoH}px` }}
                          />
                        )}
                        {/* On-call in middle */}
                        {oncallH > 0 && (
                          <div
                            className={`absolute left-0 right-0 ${day.is_past ? 'bg-orange-300' : 'bg-orange-400'}`}
                            style={{ bottom: `${regularH}px`, height: `${oncallH}px` }}
                          />
                        )}
                        {/* Regular at base */}
                        {regularH > 0 && (
                          <div
                            className={`absolute left-0 right-0 rounded-t-sm transition-all ${belowTarget ? 'bg-blue-300 ring-1 ring-red-400' : day.is_past ? 'bg-blue-300' : 'bg-blue-400'} ${isSelected ? 'ring-2 ring-blue-600' : ''}`}
                            style={{ bottom: 0, height: `${regularH}px` }}
                          />
                        )}
                      </>
                    ) : (
                      // Role breakdown stacked
                      (() => {
                        let offset = 0;
                        return roles.map(role => {
                          const count = (day.regular.by_role?.[role] || 0) + (day.on_call.by_role?.[role] || 0);
                          if (count === 0) return null;
                          const h = Math.round((count / maxCount) * barAreaHeight);
                          const bottom = offset;
                          offset += h;
                          return (
                            <div
                              key={role}
                              className={`absolute left-0 right-0 ${getRoleColor(role).bg} ${day.is_past ? 'opacity-70' : ''}`}
                              style={{ bottom: `${bottom}px`, height: `${h}px` }}
                            />
                          );
                        });
                      })()
                    )}
                  </div>

                  {/* X-axis labels */}
                  <div className={`mt-1 text-center ${isToday ? 'font-bold' : ''}`}>
                    <p className={`text-[10px] ${isToday ? 'text-blue-600' : day.is_past ? 'text-gray-300' : 'text-gray-500'}`}>
                      {day.day_of_week.substring(0, 3)}
                    </p>
                    <p className={`text-[10px] ${isToday ? 'text-blue-600 font-bold' : day.is_past ? 'text-gray-300' : 'text-gray-400'}`}>
                      {day.date.substring(5)}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

const WeeklyChart = ({ weeks, maxCount, chartHeight, showTarget, targetValue, formatDate }) => {
  return (
    <div className="flex">
      <div className="w-8 flex flex-col justify-between pr-1" style={{ height: `${chartHeight}px` }}>
        <span className="text-[10px] text-gray-400 text-right">{Math.round(maxCount)}</span>
        <span className="text-[10px] text-gray-400 text-right">{Math.round(maxCount / 2)}</span>
        <span className="text-[10px] text-gray-400 text-right">0</span>
      </div>
      <div className="flex-1 relative">
        {showTarget && targetValue > 0 && targetValue <= maxCount && (
          <div
            className="absolute left-0 right-0 border-t-2 border-dashed border-red-400 z-10 pointer-events-none"
            style={{ bottom: `${(targetValue / maxCount) * chartHeight}px` }}
          />
        )}
        <div className="flex items-end justify-around" style={{ height: `${chartHeight}px` }}>
          {weeks.map((week) => {
            const total = week.avg_regular + week.avg_oncall + week.avg_pto;
            const barArea = chartHeight - 30;
            const regularH = Math.round((week.avg_regular / maxCount) * barArea);
            const oncallH = Math.round((week.avg_oncall / maxCount) * barArea);
            const ptoH = Math.round((week.avg_pto / maxCount) * barArea);
            return (
              <div key={week.week_number} className="flex flex-col items-center group" style={{ minWidth: '80px' }}>
                <div className="opacity-0 group-hover:opacity-100 transition-opacity text-xs text-gray-600 font-medium mb-1">
                  avg {Math.round(total)}
                </div>
                <div className="relative" style={{ width: '56px', height: `${barArea}px` }}>
                  {ptoH > 0 && (
                    <div
                      className="absolute left-0 right-0 bg-yellow-400 rounded-t"
                      style={{ bottom: `${regularH + oncallH}px`, height: `${ptoH}px` }}
                    />
                  )}
                  {oncallH > 0 && (
                    <div
                      className="absolute left-0 right-0 bg-orange-400"
                      style={{ bottom: `${regularH}px`, height: `${oncallH}px` }}
                    />
                  )}
                  {regularH > 0 && (
                    <div
                      className="absolute left-0 right-0 bg-blue-400 rounded-t-sm"
                      style={{ bottom: 0, height: `${regularH}px` }}
                    />
                  )}
                </div>
                <div className="mt-1 text-center">
                  <p className="text-xs font-medium text-gray-700">Wk {week.week_number}</p>
                  <p className="text-[10px] text-gray-400">{formatDate(week.start_date)}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const DayDrillDown = ({ day, onClose, formatDateFull, getRoleColor }) => {
  if (!day) return null;

  const ShiftList = ({ title, icon: Icon, iconColor, shifts, emptyText }) => (
    <div>
      <div className="flex items-center space-x-2 mb-2">
        <Icon className={`h-4 w-4 ${iconColor}`} />
        <span className="text-sm font-medium text-gray-700">{title}</span>
        <Badge variant="secondary" className="text-xs">{shifts.length}</Badge>
      </div>
      {shifts.length === 0 ? (
        <p className="text-xs text-gray-400 italic pl-6">{emptyText}</p>
      ) : (
        <div className="space-y-1 pl-6">
          {shifts.map((emp, i) => (
            <div key={`${emp.name}-${emp.role}-${emp.start_time}-${i}`} className="flex items-center justify-between py-1.5 px-3 bg-gray-50 rounded text-sm">
              <div className="flex items-center space-x-2">
                <span className="font-medium text-gray-900">{emp.name}</span>
                <span className={`text-xs px-1.5 py-0.5 rounded ${getRoleColor(emp.role).light} ${getRoleColor(emp.role).text}`}>
                  {emp.role}
                </span>
              </div>
              <span className="text-xs text-gray-500">{emp.start_time} - {emp.end_time}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <Card data-testid="day-drilldown">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">
            {formatDateFull(day.date)}
            {day.is_today && <Badge className="ml-2 bg-blue-100 text-blue-700 text-xs">Today</Badge>}
            {day.is_past && <Badge variant="secondary" className="ml-2 text-xs">Past</Badge>}
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="close-drilldown">
            <X className="h-4 w-4" />
          </Button>
        </div>
        {/* Summary badges */}
        <div className="flex items-center gap-2 mt-2">
          <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-700 font-medium">
            {day.regular.count} Regular
          </span>
          <span className="text-xs px-2 py-1 rounded-full bg-orange-100 text-orange-700 font-medium">
            {day.on_call.count} On-Call
          </span>
          <span className="text-xs px-2 py-1 rounded-full bg-yellow-100 text-yellow-700 font-medium">
            {day.pto.count} PTO
          </span>
          {day.wfh.count > 0 && (
            <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700 font-medium">
              {day.wfh.count} WFH
            </span>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <ShiftList title="Regular Shifts" icon={Users} iconColor="text-blue-500" shifts={day.regular.employees} emptyText="No regular shifts" />
        <ShiftList title="On-Call" icon={Phone} iconColor="text-orange-500" shifts={day.on_call.employees} emptyText="No on-call coverage" />
        {day.pto.employees.length > 0 && (
          <ShiftList title="PTO" icon={Plane} iconColor="text-yellow-500" shifts={day.pto.employees} emptyText="" />
        )}
        {day.wfh.employees.length > 0 && (
          <div>
            <div className="flex items-center space-x-2 mb-2">
              <Home className="h-4 w-4 text-green-500" />
              <span className="text-sm font-medium text-gray-700">Working from Home</span>
              <Badge variant="secondary" className="text-xs">{day.wfh.count}</Badge>
            </div>
            <div className="space-y-1 pl-6">
              {day.wfh.employees.map((emp, i) => (
                <div key={`${emp.name}-${emp.role}-${i}`} className="flex items-center space-x-2 py-1.5 px-3 bg-green-50 rounded text-sm">
                  <span className="font-medium text-gray-900">{emp.name}</span>
                  <span className={`text-xs px-1.5 py-0.5 rounded ${getRoleColor(emp.role).light} ${getRoleColor(emp.role).text}`}>
                    {emp.role}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Role breakdown for this day */}
        <div className="border-t border-gray-100 pt-3">
          <p className="text-sm font-medium text-gray-700 mb-2">By Role</p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(day.regular.by_role || {}).sort((a, b) => b[1] - a[1]).map(([role, count]) => (
              <span key={role} className={`text-xs px-2 py-1 rounded-full ${getRoleColor(role).light} ${getRoleColor(role).text} font-medium`}>
                {role}: {count}
              </span>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const AVG_PRESETS = [
  { key: 'last7', label: 'Last 7 days' },
  { key: 'last30', label: 'Last 30 days' },
  { key: 'last90', label: 'Last 90 days' },
  { key: 'thisMonth', label: 'This month' },
  { key: 'lastMonth', label: 'Last month' },
  { key: 'thisQuarter', label: 'This quarter' },
];

const computeAvgPreset = (key) => {
  const today = new Date();
  let start, end;
  switch (key) {
    case 'last7': end = new Date(today); start = new Date(today); start.setDate(today.getDate() - 6); break;
    case 'last30': end = new Date(today); start = new Date(today); start.setDate(today.getDate() - 29); break;
    case 'last90': end = new Date(today); start = new Date(today); start.setDate(today.getDate() - 89); break;
    case 'thisMonth': start = new Date(today.getFullYear(), today.getMonth(), 1); end = new Date(today.getFullYear(), today.getMonth() + 1, 0); break;
    case 'lastMonth': start = new Date(today.getFullYear(), today.getMonth() - 1, 1); end = new Date(today.getFullYear(), today.getMonth(), 0); break;
    case 'thisQuarter': { const q = Math.floor(today.getMonth() / 3); start = new Date(today.getFullYear(), q * 3, 1); end = new Date(today.getFullYear(), q * 3 + 3, 0); break; }
    default: start = new Date(today); end = new Date(today);
  }
  return { start: toLocalYMD(start), end: toLocalYMD(end) };
};

const prettyRole = (r) => (r || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
const fmtHour = (h) => `${String(h).padStart(2, '0')}:00`;

const StaffingAverages = () => {
  const [roles, setRoles] = useState([]);
  const [role, setRole] = useState('');
  const initial = computeAvgPreset('last30');
  const [startDate, setStartDate] = useState(initial.start);
  const [endDate, setEndDate] = useState(initial.end);
  const [activePreset, setActivePreset] = useState('last30');
  const [includeOnCall, setIncludeOnCall] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadRoles = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/role-permissions`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { active_only: true }
        });
        const list = (res.data || []).map((p) => p.role).filter(Boolean).sort();
        setRoles(list);
        if (list.length && !role) setRole(list[0]);
      } catch (e) {
        console.error('Failed to load roles:', e);
      }
    };
    loadRoles();
  }, []);

  const fetchAverages = async () => {
    if (!role) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const shift_types = includeOnCall ? 'regular,on_call' : 'regular';
      const res = await axios.get(`${API}/staffing-averages`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { role, start_date: startDate, end_date: endDate, shift_types }
      });
      setResult(res.data);
    } catch (e) {
      console.error('Failed to load staffing averages:', e);
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (role) fetchAverages();
  }, [role, startDate, endDate, includeOnCall]);

  const applyPreset = (key) => {
    const { start, end } = computeAvgPreset(key);
    setActivePreset(key);
    setStartDate(start);
    setEndDate(end);
  };

  const maxHour = useMemo(() => {
    if (!result) return 1;
    return Math.max(...result.by_hour.map((h) => h.avg), 0.1);
  }, [result]);

  return (
    <div className="space-y-4" data-testid="staffing-averages">
      {/* Controls */}
      <Card>
        <CardContent className="pt-4 pb-4 space-y-4">
          <div className="flex flex-wrap items-end gap-4">
            {/* Role */}
            <div className="flex flex-col space-y-1">
              <label className="text-xs font-medium text-gray-500">Role</label>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value)}
                data-testid="averages-role-select"
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg bg-white min-w-[160px] focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {roles.length === 0 && <option value="">No roles</option>}
                {roles.map((r) => (
                  <option key={r} value={r}>{prettyRole(r)}</option>
                ))}
              </select>
            </div>

            {/* Start date */}
            <div className="flex flex-col space-y-1">
              <label className="text-xs font-medium text-gray-500">From</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => { setStartDate(e.target.value); setActivePreset(null); }}
                data-testid="averages-start-date"
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* End date */}
            <div className="flex flex-col space-y-1">
              <label className="text-xs font-medium text-gray-500">To</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => { setEndDate(e.target.value); setActivePreset(null); }}
                data-testid="averages-end-date"
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Include On-Call */}
            <label className="flex items-center space-x-2 pb-1.5 cursor-pointer" data-testid="averages-oncall-toggle-label">
              <Checkbox
                checked={includeOnCall}
                onCheckedChange={(v) => setIncludeOnCall(!!v)}
                data-testid="averages-oncall-toggle"
              />
              <span className="text-sm text-gray-700">Include On-Call</span>
            </label>

            <Button onClick={fetchAverages} variant="outline" size="sm" disabled={loading || !role} data-testid="averages-refresh">
              <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {/* Presets */}
          <div className="flex flex-wrap items-center gap-2">
            <CalendarRange className="h-3.5 w-3.5 text-gray-400" />
            {AVG_PRESETS.map((p) => (
              <button
                key={p.key}
                onClick={() => applyPreset(p.key)}
                data-testid={`averages-preset-${p.key}`}
                className={`px-2.5 py-1 text-xs font-medium rounded-full border transition-all ${
                  activePreset === p.key ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {loading && !result ? (
        <div className="flex items-center justify-center h-40">
          <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      ) : !result ? (
        <p className="text-sm text-gray-400 py-8 text-center">Select a role and date range to view averages.</p>
      ) : result.total_shifts === 0 ? (
        <Card>
          <CardContent className="py-10 text-center">
            <p className="text-sm text-gray-500" data-testid="averages-empty">
              No {result.shift_types.join(' / ')} shifts for {prettyRole(result.role)} in this date range.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Summary */}
          <div className="flex flex-wrap items-center gap-2" data-testid="averages-summary">
            <Badge className="bg-blue-100 text-blue-700">{prettyRole(result.role)}</Badge>
            <Badge variant="secondary">{result.active_days} active day{result.active_days === 1 ? '' : 's'}</Badge>
            <Badge variant="secondary">{result.total_shifts} shifts</Badge>
            {result.shift_types.map((t) => (
              <Badge key={t} variant="outline" className="capitalize">{t.replace('_', ' ')}</Badge>
            ))}
          </div>

          {/* By Shift Window */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center space-x-2">
                <Layers className="h-4 w-4 text-blue-600" />
                <span>Average Staffing by Shift Window</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="averages-by-shift-table">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 px-3 text-gray-600 font-medium">Shift</th>
                      <th className="text-left py-2 px-3 text-gray-600 font-medium">Window</th>
                      <th className="text-center py-2 px-3 text-gray-600 font-medium">Total Shifts</th>
                      <th className="text-center py-2 px-3 text-gray-600 font-medium">Avg / Active Day</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.by_shift.map((s) => (
                      <tr key={`${s.start_time}-${s.end_time}`} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`averages-shift-row-${s.start_time}-${s.end_time}`}>
                        <td className="py-2 px-3 font-medium text-gray-900">
                          {s.matched ? s.label : <span className="text-gray-500 italic">Unnamed</span>}
                        </td>
                        <td className="py-2 px-3 text-gray-600">{s.start_time}–{s.end_time}</td>
                        <td className="py-2 px-3 text-center">{s.total}</td>
                        <td className="py-2 px-3 text-center font-semibold text-blue-700">{s.avg_per_day}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-[11px] text-gray-400 mt-2">Averaged over {result.active_days} day(s) that had at least one matching shift.</p>
            </CardContent>
          </Card>

          {/* By Hour */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center space-x-2">
                <Clock className="h-4 w-4 text-blue-600" />
                <span>Average Staff on Duty by Hour</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Bar chart */}
              <div className="flex items-end gap-1 h-40 border-b border-gray-200 pb-0" data-testid="averages-by-hour-chart">
                {result.by_hour.map((h) => {
                  const barH = Math.round((h.avg / maxHour) * 140);
                  return (
                    <div key={h.hour} className="flex-1 flex flex-col items-center justify-end group" style={{ minWidth: '18px' }}>
                      <div className="opacity-0 group-hover:opacity-100 text-[10px] text-gray-600 font-medium mb-0.5">{h.avg}</div>
                      <div
                        className="w-full bg-blue-400 hover:bg-blue-500 rounded-t transition-all"
                        style={{ height: `${barH}px` }}
                        data-testid={`averages-hour-bar-${h.hour}`}
                      />
                    </div>
                  );
                })}
              </div>
              <div className="flex gap-1 mt-1">
                {result.by_hour.map((h) => (
                  <div key={h.hour} className="flex-1 text-center" style={{ minWidth: '18px' }}>
                    <span className="text-[9px] text-gray-400">{h.hour % 3 === 0 ? fmtHour(h.hour) : ''}</span>
                  </div>
                ))}
              </div>

              {/* Table */}
              <div className="overflow-x-auto mt-4">
                <table className="w-full text-sm" data-testid="averages-by-hour-table">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 px-3 text-gray-600 font-medium">Hour</th>
                      <th className="text-center py-2 px-3 text-gray-600 font-medium">Total (staff-shifts)</th>
                      <th className="text-center py-2 px-3 text-gray-600 font-medium">Avg / Active Day</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.by_hour.filter((h) => h.total > 0).map((h) => (
                      <tr key={h.hour} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-2 px-3 font-medium">{fmtHour(h.hour)} – {fmtHour((h.hour + 1) % 24)}</td>
                        <td className="py-2 px-3 text-center">{h.total}</td>
                        <td className="py-2 px-3 text-center font-semibold text-blue-700">{h.avg}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default StaffingLevels;
